// RuleInputEditor.tsx
import React, { useEffect, useState } from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  LexicalNode,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  COPY_COMMAND,
  CUT_COMMAND,
  KEY_DOWN_COMMAND,
  PASTE_COMMAND,
} from "lexical";

import { Tag } from "../interfaces/Tag";
import { $createTagNode, $isTagNode, TagNode } from "./lexical/TagNode";
import { $createOperatorNode, OperatorNode } from "./lexical/OperatorNode";
import { tokenizeScriptForRendering } from "../../utils/grammar/RuleGrammarParser";

import { OperatorToolbarPlugin } from "./lexical/OperatorToolbarPlugin";
import { TagDropdownPlugin } from "./lexical/TagDropdownPlugin";
import { OnChangePlugin } from "./lexical/OnChangePlugin";
import { RestrictInputPlugin } from "./lexical/RestrictInputPlugin";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";

export interface RuleInputEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  tags?: Tag[];
  label?: string;
  placeholder?: string;
  disabled?: boolean;
}

const TAG_DISPLAY_PREFIX_REGEX = /^TAG:\s*/i;

function stripTagDisplayPrefix(value?: string): string {
  return (value ?? "").trim().replace(TAG_DISPLAY_PREFIX_REGEX, "").trim();
}

const styles = {
  container: {
    width: "100%",
  } as React.CSSProperties,
  label: {
    display: "block",
    margin: "0 0 4px 0",
    fontSize: "14px",
    fontWeight: 500,
  } as React.CSSProperties,
  headerContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "4px 8px",
    backgroundColor: "#fafafa",
    borderRadius: "4px 4px 0 0",
    border: "1px solid #d0d0d0",
    borderBottom: "none",
    minHeight: "28px",
  } as React.CSSProperties,
  editorContainer: {
    position: "relative",
    minHeight: "100px",
    padding: "8px",
    border: "1px solid #d0d0d0",
    borderTop: "none",
    borderRadius: "0 0 4px 4px",
    fontSize: "12px",
    lineHeight: "20px",
    fontFamily: "Segoe UI, -apple-system, BlinkMacSystemFont, Roboto, Oxygen, Ubuntu, sans-serif",
    backgroundColor: "white",
    cursor: "text",
    outline: "none",
  } as React.CSSProperties,
  placeholder: {
    position: "absolute",
    top: "8px",
    left: "8px",
    color: "#a19f9d",
    pointerEvents: "none",
    userSelect: "none",
  } as React.CSSProperties,
};

/**
 * PreventClipboardPlugin:
 * Blocks paste/copy/cut via Lexical commands.
 */
const PreventClipboardPlugin: React.FC = () => {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    const unregisters = [
      editor.registerCommand(
        PASTE_COMMAND,
        (event) => {
          event?.preventDefault?.();
          return true;
        },
        COMMAND_PRIORITY_HIGH
      ),
      editor.registerCommand(
        COPY_COMMAND,
        (event) => {
          event?.preventDefault?.();
          return true;
        },
        COMMAND_PRIORITY_HIGH
      ),
      editor.registerCommand(
        CUT_COMMAND,
        (event) => {
          event?.preventDefault?.();
          return true;
        },
        COMMAND_PRIORITY_HIGH
      ),
    ];
    return () => unregisters.forEach((u) => u());
  }, [editor]);

  return null;
};

/** Mappa da carattere digitato → { simbolo, spazi laterali } */
const KEY_TO_OPERATOR: Record<string, { symbol: string; pad: boolean }> = {
  "+": { symbol: "+", pad: true },
  "-": { symbol: "-", pad: true },
  "*": { symbol: "*", pad: true },
  "/": { symbol: "/", pad: true },
  "(": { symbol: "(", pad: true },
  ")": { symbol: ")", pad: true },
  ">": { symbol: ">", pad: true },
  "<": { symbol: "<", pad: true },
  "=": { symbol: "=", pad: true },
};

/**
 * OperatorTransformPlugin:
 * Intercetta i tasti operatori PRIMA che Lexical li inserisca come TextNode.
 * Inserisce direttamente un OperatorNode con gli stessi spazi laterali
 * che usa il toolbar, lasciando intatti tutti i TextNode adiacenti.
 */
const OperatorTransformPlugin: React.FC = () => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(
      KEY_DOWN_COMMAND,
      (event: KeyboardEvent) => {
        const op = KEY_TO_OPERATOR[event.key];
        if (!op) return false;
        if (event.ctrlKey || event.metaKey || event.altKey) return false;

        event.preventDefault();
        editor.update(() => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection)) return;
          if (op.pad) selection.insertText(" ");
          selection.insertNodes([$createOperatorNode(op.symbol)]);
          if (op.pad) selection.insertText(" ");
        });
        return true;
      },
      COMMAND_PRIORITY_LOW
    );
  }, [editor]);

  return null;
};

/**
 * Initialize content:
 * - Inserts plain text
 * - Converts {{tagId}} to TagNode
 * - Displays "!=" as "≠"
 */
const InitializePlugin: React.FC<{ value: string; tags: Tag[] }> = ({ value, tags }) => {
  const [editor] = useLexicalComposerContext();
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (initialized) return;

    editor.update(() => {
      const root = $getRoot();
      root.clear();

      const paragraph = $createParagraphNode();
      const displayValue = (value ?? "").replace(/!=/g, "≠");
      const tagMap = new Map(tags.map((t) => [t.id, t]));

      for (const token of tokenizeScriptForRendering(displayValue)) {
        if (token.type === "TAG") {
          const tag = tagMap.get(token.tagId);
          const tagLabel = stripTagDisplayPrefix(tag?.label ?? token.tagId) || token.tagId;
          const hasObjects = Array.isArray(tag?.objects) && tag.objects.length > 0;
          const isDeleted = tag?.isDeleted ?? false;
          paragraph.append($createTagNode(token.tagId, tagLabel, hasObjects, isDeleted));
        } else if (token.type === "OPERATOR") {
          paragraph.append($createOperatorNode(token.text));
        } else {
          paragraph.append($createTextNode(token.text));
        }
      }

      root.append(paragraph);
    });

    setInitialized(true);
  }, [editor, value, tags, initialized]);

  return null;
};

const SyncTagsPlugin: React.FC<{ tags: Tag[] }> = ({ tags }) => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    editor.update(
      () => {
        const root = $getRoot();
        const tagMap = new Map(tags.map((tag) => [tag.id, tag]));

        const syncNode = (node: LexicalNode) => {
          if ($isTagNode(node)) {
            const tag = tagMap.get(node.__tagId);
            const normalizedTagLabel = stripTagDisplayPrefix(tag?.label);
            const tagLabel = tag ? normalizedTagLabel : stripTagDisplayPrefix(node.__tagLabel) || node.__tagLabel;
            const hasObjects = tag ? Array.isArray(tag.objects) && tag.objects.length > 0 : node.__hasObjects;
            node.setTagData(tagLabel, hasObjects);
            return;
          }

          if ("getChildren" in node && typeof node.getChildren === "function") {
            node.getChildren().forEach(syncNode);
          }
        };

        root.getChildren().forEach(syncNode);
      },
      { tag: "collaboration" }
    );
  }, [editor, tags]);

  return null;
};

export const RuleInputEditor: React.FC<RuleInputEditorProps> = ({
  value = "",
  onChange,
  tags = [],
  label,
  placeholder = "",
  disabled = false,
}) => {
  const initialConfig = {
    namespace: "RuleInputEditor",
    nodes: [TagNode, OperatorNode],
    editable: !disabled,
    onError: (error: Error) => {
      console.error("[RuleInputEditor] Lexical error:", error);
    },
    theme: {
      paragraph: "lexical-paragraph",
      text: {
        bold: "lexical-text-bold",
        italic: "lexical-text-italic",
      },
    },
  };

  // DOM-level prevention (defense in depth).
  const preventDomClipboard = {
    onPaste: (e: React.ClipboardEvent) => e.preventDefault(),
    onCopy: (e: React.ClipboardEvent) => e.preventDefault(),
    onCut: (e: React.ClipboardEvent) => e.preventDefault(),
    onDrop: (e: React.DragEvent) => e.preventDefault(),
    onDragStart: (e: React.DragEvent) => e.preventDefault(),
    onDragOver: (e: React.DragEvent) => e.preventDefault(),
    onContextMenu: (e: React.MouseEvent) => e.preventDefault(),
  };

  return (
    <div style={styles.container}>
      {label && <label style={styles.label}>{label}</label>}

      <LexicalComposer initialConfig={initialConfig}>
        {/* Header: Tag dropdown + operators toolbar */}
        <div style={styles.headerContainer}>
          <TagDropdownPlugin tags={tags.filter((t) => !t.isDeleted)} disabled={disabled} />
          <OperatorToolbarPlugin disabled={disabled} />
        </div>

        {/* Editor container */}
        <div style={styles.editorContainer}>
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                style={{ fontSize: "14px", outline: "none", minHeight: "84px" }}
                spellCheck={false}
                {...preventDomClipboard}
              />
            }
            placeholder={<div style={styles.placeholder}>{placeholder}</div>}
            ErrorBoundary={LexicalErrorBoundary}
          />

          <HistoryPlugin />
          <RestrictInputPlugin />
          <OperatorTransformPlugin />
          <OnChangePlugin onChange={onChange} />

          {/* Prevent clipboard operations inside editor */}
          <PreventClipboardPlugin />

          {/* Initialize with value + tags */}
          <InitializePlugin value={value} tags={tags} />
          <SyncTagsPlugin tags={tags} />
        </div>
      </LexicalComposer>
    </div>
  );
};
